import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { updateUser as updateUserApi, getEditUserForm } from '../../../models/userModel';
import { useAuth } from '../../../context/AuthContext';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import SearchableSelect from '../../../components/common/StyledSelect';
import api from '../../../services/api';

const schema = yup.object().shape({
  full_name: yup.string().required('Full Name is required'),
  email: yup.string().email('Invalid email format').required('Email is required'),
  phone: yup.string().nullable(),
  role: yup.number().required('Role is required').positive().integer(),
  department_id: yup.number().required('Department is required').positive().integer(),
  division_department: yup.number().required('Division is required').positive().integer(),
  password: yup.string().nullable(), // Password is optional for updates
  user_delegate: yup.number().nullable(),
  user_otp: yup.number().nullable(), // OTP delivery preference: 1=SMS, 2=Email, 3=Both
  user_procurement_otp: yup.boolean(),
  it_support: yup.boolean(),
  supervisor: yup.boolean(),
  manager: yup.boolean(),
  hod: yup.boolean(),
  director: yup.boolean(),
});

function UserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: authUser, updateUser } = useAuth();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [actualDepartments, setActualDepartments] = useState([]);
  const [allDivisions, setAllDivisions] = useState([]);
  const [filteredDivisions, setFilteredDivisions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      full_name: '',
      email: '',
      phone: '',
      role: null,
      department_id: null,
      division_department: null,
      password: '',
      user_delegate: null,
      user_otp: 1, // Default to SMS
      user_procurement_otp: false,
      it_support: false,
      supervisor: false,
      manager: false,
      hod: false,
      director: false,
    }
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getEditUserForm(id);
        if (response.status === 1 && response.data) {
          const userData = response.data.row;
          setUser(userData);
          setRoles(response.data.roles || []);
          const divisions = response.data.departments || []; // These are actually divisions
          console.log('Divisions Data:', divisions);
          setAllDivisions(divisions);
          setFilteredDivisions(divisions); // Show all divisions initially when editing
          setUsers(response.data.users || []);
          
          // Fetch actual departments
          try {
            const deptResponse = await api.get('/api/departments/categories');
            console.log('Full API Response:', deptResponse);
            if (deptResponse.data.status === 1 && deptResponse.data.data) {
              const departmentsData = deptResponse.data.data.data || deptResponse.data.data;
              console.log('Extracted Departments Data:', departmentsData);
              setActualDepartments(departmentsData);
            }
          } catch (error) {
            console.error('Error fetching departments:', error);
          }
          
          // Reset form with user data
          reset({
            full_name: userData.user_full_name || '',
            email: userData.user_email || '',
            phone: userData.user_phone || '',
            role: userData.role !== undefined && userData.role !== null && userData.role !== '' ? Number(userData.role) : null,
            division_department: userData.department !== undefined && userData.department !== null && userData.department !== '' ? Number(userData.department) : null,
            department_id: userData.department_id !== undefined && userData.department_id !== null && userData.department_id !== '' ? Number(userData.department_id) : null,
            password: '',
            user_delegate: userData.user_delegate ? Number(userData.user_delegate) : null,
            user_otp: userData.user_otp ? Number(userData.user_otp) : 1,
            user_procurement_otp: userData.user_procurement_otp == 1,
            it_support: userData.it_support == 1,
            supervisor: userData.supervisor == 1,
            manager: userData.manager == 1,
            hod: userData.hod == 1,
            director: userData.director == 1,
          });
          
          // Set selectedDepartmentId to pre-select department and filter divisions
          if (userData.department_id) {
            setSelectedDepartmentId(Number(userData.department_id));
            // Filter divisions based on the user's department
            const filtered = divisions.filter(
              d => d.division_department === String(userData.department_id) || !d.division_department || d.department_id === '15'
            );
            setFilteredDivisions(filtered);
          }
        } else {
          toast.error("Failed to load user data.");
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        toast.error("Failed to load user data.");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id, reset]);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      
      // Transform data to match backend expectations
      const submitData = {
        ...data,
        department: data.division_department, // Backend expects 'department' to be the division ID
        department_id: data.department_id // Actual department category ID
      };
      
      // Remove the fields we transformed to avoid confusion
      delete submitData.division_department;
      
      const response = await updateUserApi(id, submitData);
      if (response.status === 1) {
        toast.success('User updated successfully!');
        if (authUser.user_id === parseInt(id)) { 
          updateUser(response.data);
        }
        navigate('..', { state: { fromForm: true } });
      } else {
        toast.error(response.message || 'Failed to update user.');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Handle department change to filter divisions
  const handleDepartmentChange = useCallback((departmentId) => {
    // Track selected department for form state, but DO NOT change the divisions list.
    // Division list should always be fully populated and not depend on Department selection.
    setSelectedDepartmentId(departmentId);
  }, [allDivisions]);

  // Prepare options for dropdowns (must be before early returns due to React Hooks rules)
  const roleOptions = roles.map(r => ({ value: r.role_id, label: r.role_name }));
  const departmentOptions = Array.isArray(actualDepartments) ? actualDepartments.map(d => ({ 
    value: d.category_id, 
    label: d.category_name 
  })) : [];
  // Division dropdown must always show all known divisions regardless of selected department
  const divisionOptions = Array.isArray(allDivisions) ? allDivisions.map(d => ({
    value: d.department_id,
    label: d.department_name,
  })) : [];
  
  const delegateOptions = useMemo(() => {
    if (!users || !Array.isArray(users) || !user) return [];
    const filteredUsers = users.filter(u => u.user_id !== user.user_id);
    return [
      { value: 0, label: 'None' },
      ...filteredUsers.map(u => ({ value: u.user_id, label: u.user_full_name }))
    ];
  }, [users, user]);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-[#0087D4]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading user...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">User not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-2xl font-semibold text-gray-900">Edit User</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: User Settings */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow border overflow-hidden">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Settings</h3>
              <div className="space-y-4">
                <div className="overflow-hidden">
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                          Role *
                        </label>
                        <div className="w-full overflow-hidden">
                          <SearchableSelect
                            id="role"
                            options={roleOptions}
                            value={field.value}
                            onChange={(option) => field.onChange(option?.value || null)}
                            placeholder="Select role..."
                            className="mt-1"
                          />
                        </div>
                        {errors.role && (
                          <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                          New Password (optional)
                        </label>
                        <input
                          id="password"
                          type="password"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                          placeholder="Leave blank to keep current password"
                          {...field}
                        />
                        {errors.password && (
                          <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="overflow-hidden">
                  <Controller
                    name="user_delegate"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="user_delegate" className="block text-sm font-medium text-gray-700 mb-1">
                          Delegate
                        </label>
                        <div className="w-full overflow-hidden">
                          <SearchableSelect
                            id="user_delegate"
                            options={delegateOptions}
                            value={field.value}
                            onChange={(option) => field.onChange(option?.value || null)}
                            placeholder="Select a delegate..."
                            className="mt-1"
                          />
                        </div>
                        {errors.user_delegate && (
                          <p className="mt-1 text-sm text-red-600">{errors.user_delegate.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* OTP Preferences */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">OTP Preferences</h3>
              <Controller
                name="user_otp"
                control={control}
                render={({ field }) => (
                  <div>
                    <label htmlFor="user_otp" className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred OTP Delivery Method
                    </label>
                    <select
                      id="user_otp"
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                      value={field.value || 1}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    >
                      <option value={1}>SMS Only</option>
                      <option value={2}>Email Only</option>
                      <option value={3}>Both SMS and Email</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Choose how you want to receive login OTP codes</p>
                  </div>
                )}
              />
            </div>

            {/* Permissions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Permissions</h3>
              <div className="space-y-3">
                <Controller
                  name="user_procurement_otp"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <input
                        id="user_procurement_otp"
                        type="checkbox"
                        className="h-4 w-4 text-[#0087D4] focus:ring-[#0087D4] border-gray-300 rounded"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <label htmlFor="user_procurement_otp" className="ml-2 block text-sm text-gray-700">
                        Procurement OTP
                      </label>
                    </div>
                  )}
                />

                <Controller
                  name="it_support"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <input
                        id="it_support"
                        type="checkbox"
                        className="h-4 w-4 text-[#0087D4] focus:ring-[#0087D4] border-gray-300 rounded"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <label htmlFor="it_support" className="ml-2 block text-sm text-gray-700">
                        IT Support
                      </label>
                    </div>
                  )}
                />

                <Controller
                  name="supervisor"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <input
                        id="supervisor"
                        type="checkbox"
                        className="h-4 w-4 text-[#0087D4] focus:ring-[#0087D4] border-gray-300 rounded"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <label htmlFor="supervisor" className="ml-2 block text-sm text-gray-700">
                        Supervisor
                      </label>
                    </div>
                  )}
                />

                <Controller
                  name="manager"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <input
                        id="manager"
                        type="checkbox"
                        className="h-4 w-4 text-[#0087D4] focus:ring-[#0087D4] border-gray-300 rounded"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <label htmlFor="manager" className="ml-2 block text-sm text-gray-700">
                        Manager
                      </label>
                    </div>
                  )}
                />

                <Controller
                  name="hod"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <input
                        id="hod"
                        type="checkbox"
                        className="h-4 w-4 text-[#0087D4] focus:ring-[#0087D4] border-gray-300 rounded"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <label htmlFor="hod" className="ml-2 block text-sm text-gray-700">
                        Head of Department
                      </label>
                    </div>
                  )}
                />

                <Controller
                  name="director"
                  control={control}
                  render={({ field }) => (
                    <div className="flex items-center">
                      <input
                        id="director"
                        type="checkbox"
                        className="h-4 w-4 text-[#0087D4] focus:ring-[#0087D4] border-gray-300 rounded"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                      <label htmlFor="director" className="ml-2 block text-sm text-gray-700">
                        Director
                      </label>
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Actions</h3>
              <div className="flex flex-col gap-3">
                <button 
                  type="submit" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0087D4] hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : 'Update User'}
                </button>
                <button 
                  type="button" 
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0087D4] w-full"
                  onClick={handleCancel}
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: User Details */}
          <div className="lg:col-span-2">
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-4">User Information</h3>
              <div className="space-y-4">
                <div>
                  <Controller
                    name="full_name"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                          Full Name *
                        </label>
                        <input
                          id="full_name"
                          type="text"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                          placeholder="Enter full name"
                          {...field}
                        />
                        {errors.full_name && (
                          <p className="mt-1 text-sm text-red-600">{errors.full_name.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email Address *
                        </label>
                        <input
                          id="email"
                          type="email"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                          placeholder="Enter email address"
                          {...field}
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                          placeholder="Enter phone number"
                          {...field}
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="bg-gray-50 p-4 rounded-md">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Password Information</h4>
                  <p className="text-sm text-gray-600">
                    Leave the password field blank to keep the current password. Enter a new password only if you want to change it.
                  </p>
                </div>
              </div>
            </div>

            {/* Department & Division Card */}
            <div className="bg-white p-6 rounded-lg shadow border mt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Department & Division</h3>
              <div className="space-y-4">
                <div className="overflow-hidden">
                  <Controller
                    name="department_id"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="department_id" className="block text-sm font-medium text-gray-700 mb-1">
                          Department *
                        </label>
                        <div className="w-full overflow-hidden">
                          <SearchableSelect
                            id="department_id"
                            options={departmentOptions}
                            value={field.value}
                            onChange={(option) => {
                              const value = option?.value || null;
                              field.onChange(value);
                              handleDepartmentChange(value);
                            }}
                            placeholder="Select department..."
                            className="mt-1"
                          />
                        </div>
                        {errors.department_id && (
                          <p className="mt-1 text-sm text-red-600">{errors.department_id.message}</p>
                        )}
                      </div>
                    )}
                  />
                </div>

                <div className="overflow-hidden">
                  <Controller
                    name="division_department"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <label htmlFor="division_department" className="block text-sm font-medium text-gray-700 mb-1">
                          Division *
                        </label>
                        <div className="w-full overflow-hidden">
                          <SearchableSelect
                            id="division_department"
                            options={divisionOptions}
                            value={field.value}
                            onChange={(option) => field.onChange(option?.value || null)}
                            placeholder="Select division..."
                            className="mt-1"
                          />
                        </div>
                        {errors.division_department && (
                          <p className="mt-1 text-sm text-red-600">{errors.division_department.message}</p>
                        )}
                        {/* Division list is independent of Department selection */}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default UserEdit;